import { isSearchResultJiraIssue } from '@atlassian/search-client/jira';

import type { RecentActivityItem, SearchResultItem } from './types.tsx';

/**
 * Extracts the Jira workItem key from an activity node if the product is Jira.
 * @param activity RecentActivityItem
 * @returns The workItem key string if present and product is Jira, otherwise undefined.
 */
export const getJiraWorkItemKeyFromActivity = (
	activity: RecentActivityItem,
): string | undefined => {
	if (
		activity?.data.node.object.type === 'issue' &&
		activity?.data.node.object.product === 'jira' &&
		activity?.data.node.object.data &&
		'key' in activity?.data.node.object.data
	) {
		return activity?.data.node.object.data.key;
	}
	return undefined;
};

/**
 * Extracts the Jira workItem key from an search result if the product is Jira.
 * @param result SearchResultItem
 * @returns The workItem key string if present and product is Jira, otherwise undefined.
 */
export const getJiraWorkItemKeyFromSearchResult = (
	result: SearchResultItem,
): string | undefined => {
	if (isSearchResultJiraIssue(result.data)) {
		return result.data.issue?.key;
	}
	return undefined;
};

/**
 * Normalizes an issue key query by removing hyphens and spaces so that
 * queries like "AAA34", "AAA 34", and "AAA-34" all match the canonical key "AAA-34".
 * Returns undefined if the value does not look like an issue key.
 */
export const normalizeWorkItemKey = (value: string): string | undefined => {
	const normalized = value.replace(/^([a-zA-Z]+)[\s-]?(\d+)$/, '$1-$2').toLowerCase();
	return normalized !== value.toLowerCase() || /^[a-zA-Z]+-\d+$/.test(value)
		? normalized
		: undefined;
};
